import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DinoLogo } from '@/components/DinoLogo';
import { ArchetypeMascot } from '@/components/ArchetypeMascot';
import { supabase } from '@/integrations/supabase/client';
import { archetypeInfo, type Archetype } from '@/lib/archetype';
import { Sparkles, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export default function OnboardingResults() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    loadProfile();
  }, [user, navigate]);

  const loadProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('archetype, reading_goal, reading_habit, reading_motivation, onboarding_summary')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <DinoLogo size={64} className="mb-4 justify-center" />
          <p className="text-muted-foreground">Calculating your reading archetype...</p>
        </div>
      </div>
    );
  }

  if (!profile?.archetype) {
    navigate('/onboarding/archetype');
    return null;
  }

  const archetype = profile.archetype as Archetype;
  const info = archetypeInfo[archetype];
  const scores = profile.onboarding_summary?.scores || {};

  // Get top 3 scores for display
  const sortedScores = Object.entries(scores)
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-playful">
      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="text-center space-y-6 mb-12">
          {/* Archetype Reveal */}
          <div className="inline-block animate-fade-in">
            <Badge className="text-lg px-6 py-2 mb-4 bg-secondary/20 text-secondary-foreground border-secondary/30">
              Your Reading Archetype
            </Badge>
          </div>

          <div className="inline-flex items-center justify-center w-48 h-48 mx-auto mb-6 animate-scale-in">
            <ArchetypeMascot archetype={archetype} size="xl" />
          </div>

          <h1 className="text-5xl md:text-6xl font-heading font-bold text-foreground animate-fade-in">
            You're {['a', 'e', 'i', 'o', 'u'].includes(archetype[0].toLowerCase()) ? 'an' : 'a'}{' '}
            <span className="text-secondary">{archetype}</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in">
            {info.description}
          </p>
        </div>

        {/* Stats Card */}
        <Card className="mb-8 shadow-soft animate-fade-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Your Reading Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Goal */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">Annual Goal</span>
                <span className="text-2xl font-bold text-foreground">{profile.reading_goal} books</span>
              </div>
            </div>

            {/* Habit */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">Best Reading Time</span>
                <span className="text-lg font-semibold text-foreground">{profile.reading_habit}</span>
              </div>
            </div>

            {/* Motivation */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">What Motivates You</span>
                <span className="text-lg font-semibold text-foreground">{profile.reading_motivation}</span>
              </div>
            </div>

            {/* Archetype Scores */}
            {sortedScores.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-4">Your Archetype Mix</p>
                <div className="space-y-3">
                  {sortedScores.map(([type, score]) => {
                    const maxScore = sortedScores[0][1] as number;
                    const percentage = ((score as number) / maxScore) * 100;
                    const typeInfo = archetypeInfo[type as Archetype];
                    
                    return (
                      <div key={type}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium flex items-center gap-2">
                            <span>{typeInfo.emoji}</span>
                            {type}
                          </span>
                          <span className="text-xs text-muted-foreground">{String(score)}</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card 
            className="card-elevated cursor-pointer hover:shadow-medium transition-all"
            onClick={() => navigate('/search')}
          >
            <CardContent className="pt-6 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 mx-auto flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-heading font-semibold">See Recommendations</h3>
              <p className="text-sm text-muted-foreground">{info.cta}</p>
            </CardContent>
          </Card>

          <Card 
            className="card-elevated cursor-pointer hover:shadow-medium transition-all"
            onClick={() => navigate('/search')}
          >
            <CardContent className="pt-6 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-secondary/10 mx-auto flex items-center justify-center">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="font-heading font-semibold">Follow Readers</h3>
              <p className="text-sm text-muted-foreground">Connect with readers like you</p>
            </CardContent>
          </Card>

          <Card 
            className="card-elevated cursor-pointer hover:shadow-medium transition-all"
            onClick={() => navigate('/search')}
          >
            <CardContent className="pt-6 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-accent/10 mx-auto flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-heading font-semibold">Start Reading</h3>
              <p className="text-sm text-muted-foreground">Add your first book</p>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => {
              try {
                if (typeof window !== 'undefined') {
                  sessionStorage.setItem('onboardingComplete', '1');
                }
              } catch (error) {
                console.warn('OnboardingResults: failed to set onboardingComplete flag', error);
              }
              navigate('/');
            }}
            className="gap-2 px-8 py-6 text-lg font-heading"
          >
            Start Your Reading Journey
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </main>
    </div>
  );
}
